import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type React from "react";
import { useSearchParams } from "react-router-dom";
import { AlertTriangle, ChevronDown, ChevronRight, ExternalLink, Info, RefreshCw, ScanSearch } from "lucide-react";
import { theme, card, btnPrimary, type as text, pageHeading, pageSubtitle } from "../theme";
import { api, apiErrorMessage, ApiError } from "../api/client";
import { useCurrentUser } from "../auth/useCurrentUser";
import { useFrameworks } from "../context/FrameworksContext";
import { iso27001Citation } from "../lib/standardCitation";
import { PortalLink } from "../components/PortalLink";
import { LoadingBlock } from "../components/Spinner";
import { DetailFields, DetailSection, RecordActions } from "../components/RecordView";
import { NextSteps } from "../components/NextSteps";
import {
  ModalOverlay, ModalHeader, Field, input, textarea, EmptyState, SeverityPill, secondaryBtn,
  fmtDay, fmtDateTime, relativeDue, type AttentionItem,
} from "./resilience/shared";

// Sensitive data: where personal and sensitive data sits across Microsoft 365, cloud storage and
// connected folders — the kind and how much, never the data itself. Rules in
// backend/src/lib/dataDiscovery.ts; routes in routes/dataDiscovery.routes.ts.

type Status = "open" | "mapped" | "expected" | "remediated";
type SourceId = "purview" | "macie" | "gcp_dlp" | "storage_scan";

interface Finding {
  id: string;
  source: SourceId | string;
  externalRef: string;
  location: string;
  locationKind: string;
  locationUrl: string | null;
  categories: Record<string, number>;
  detail: { types?: Record<string, number> } | null;
  itemCount: number;
  severity: "high" | "medium" | "low" | string;
  status: Status | string;
  ropaEntry: { id: string; name: string; dataCategories?: string[] } | null;
  asset: { id: string; name: string } | null;
  /** The scanned file's own name and link: sent only to members holding privacy:write. */
  fileName?: string | null;
  fileUrl?: string | null;
  /** The source stopped reporting it; never taken to mean the data has gone. */
  noLongerReportedAt?: string | null;
  reviewNote: string | null;
  reviewedAt: string | null;
  firstSeenAt: string;
  lastSeenAt: string;
  resolvedAt: string | null;
}

interface Run {
  id: string;
  source: string;
  status: string;
  startedAt: string;
  finishedAt: string | null;
  scanned: number;
  findings: number;
  error: string | null;
}

interface Confirmation {
  scope: string;
  lawfulBasis: string;
  basisNote: string;
  staffNotice: string;
  dpiaDecision: string;
  dpiaNote: string | null;
  confirmedAt: string;
  confirmedBy: { id: string; name: string } | null;
}

interface Source {
  id: SourceId;
  label: string;
  needs: string;
  coverage?: string;
  connected: boolean;
  enabled: boolean;
  lastRun: Run | null;
  confirmation?: Confirmation | null;
}

type ConfirmScope = "storage_scan" | "purview";
interface Choice { id: string; label: string }

interface Category { id: string; label: string; help: string; weight: number }

interface DiscoveryResponse {
  findings: Finding[];
  runs: Run[];
  attention: AttentionItem[];
  ropaEntries: Array<{ id: string; name: string; dataCategories: unknown }>;
  /** The asset-register rows a location can be linked to (this organisation's own). */
  assets?: Array<{ id: string; name: string; kind: string }>;
  sources: Source[];
  vocabulary: { categories: Category[]; statuses: string[]; lawfulBases?: Choice[]; dpiaDecisions?: Choice[] };
  /** False when the member does not hold privacy:write: OneDrive locations are then not named. */
  locationDetail?: boolean;
  /** False where the edition keeps no records of processing (the Jersey edition): no RoPA link is offered. */
  recordsOfProcessing?: boolean;
}

const STATUS_LABEL: Record<string, string> = {
  open: "Still to review",
  mapped: "Linked to a record",
  expected: "Expected",
  remediated: "Removed or fixed",
};

const KIND_LABEL: Record<string, string> = {
  sharepoint: "SharePoint",
  onedrive: "OneDrive",
  exchange: "Email",
  teams: "Teams",
  s3: "S3 bucket",
  gcs: "Cloud Storage bucket",
  bigquery: "BigQuery",
  google_drive: "Google Drive",
  file: "Folder",
};

const VENDOR_SOURCES: SourceId[] = ["purview", "macie", "gcp_dlp"];
const DANGER_CATS = new Set(["special_category", "health", "criminal_offence", "credentials", "payment_card"]);
/** Art. 9 or Art. 10 data: accepted as expected only on a processing record that declares it. */
const SENSITIVE_CATS = ["special_category", "health", "criminal_offence"];
const WARNING_CATS = new Set(["bank_account", "national_id"]);

const pill = (bg: string, color: string): React.CSSProperties => ({ fontSize: 11, fontWeight: 600, padding: "2px 9px", borderRadius: 2, background: bg, color, whiteSpace: "nowrap" });
const th: React.CSSProperties = { padding: "10px 14px", borderBottom: "1px solid var(--c-line)", fontWeight: 600 };
const td: React.CSSProperties = { padding: "10px 14px", verticalAlign: "top" };
const headRow: React.CSSProperties = { textAlign: "left", color: theme.color.subtle, fontSize: text.label.fontSize };
const sectionTitle: React.CSSProperties = { ...text.sectionTitle, margin: 0 };
const SEV_DOT: Record<AttentionItem["severity"], string> = { high: "var(--c-danger)", medium: "var(--c-warning)", low: "var(--c-borderStrong)" };

function statusStyle(s: string): { bg: string; color: string } {
  if (s === "open") return { bg: theme.color.warningBg, color: theme.color.warningText };
  if (s === "mapped") return { bg: theme.color.successBg, color: theme.color.successText };
  if (s === "expected") return { bg: theme.color.accentBg, color: theme.color.accentText };
  return { bg: theme.color.panelAlt, color: theme.color.muted };
}

function catStyle(id: string): { bg: string; color: string } {
  if (DANGER_CATS.has(id)) return { bg: theme.color.dangerBg, color: theme.color.dangerText };
  if (WARNING_CATS.has(id)) return { bg: theme.color.warningBg, color: theme.color.warningText };
  return { bg: theme.color.panelAlt, color: theme.color.muted };
}

const safeHttps = (url: string | null): string | null => (url && /^https:\/\//i.test(url) ? url : null);
const has = (f: Finding, cat: string) => (f.categories?.[cat] ?? 0) > 0;
const matchesCategory = (f: Finding, cat: string) => (cat === "special" ? has(f, "special_category") || has(f, "health") : has(f, cat));

// ── Page ──────────────────────────────────────────────────────────────────────

export function DataDiscoveryPage() {
  const { enrolled } = useFrameworks();
  const { user } = useCurrentUser();
  const can = (p: string) => !!user?.permissions?.includes(p);
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState<DiscoveryResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [source, setSource] = useState("");
  const [q, setQ] = useState("");
  const [reviewFor, setReviewFor] = useState<Finding | null>(null);
  // Recording why a monitoring source reads content; `then` re-sends the change that needed it.
  const [confirmFor, setConfirmFor] = useState<{ scope: ConfirmScope; then?: (c: Record<string, unknown>) => Promise<void> } | null>(null);

  const [runState, setRunState] = useState<"idle" | "busy" | "queued">("idle");
  const [runError, setRunError] = useState<string | null>(null);
  const reloadTimer = useRef<number | null>(null);

  const category = params.get("category") ?? "";
  const status = params.get("status") ?? "";
  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next, { replace: true });
  };
  // ?finding=<id> opens that location's read-only view.
  const expanded = params.get("finding");
  const setExpanded = (id: string | null) => setParam("finding", id ?? "");

  const load = useCallback(() => {
    api.get<DiscoveryResponse>("/data-discovery")
      .then((d) => { setData(d); setError(null); })
      .catch((e) => setError(apiErrorMessage(e, "Could not load sensitive data findings")));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => () => { if (reloadTimer.current) window.clearTimeout(reloadTimer.current); }, []);
  // ?confirm=storage_scan|purview (from My Actions) opens the record of the basis for that source.
  const confirmParam = params.get("confirm");
  useEffect(() => {
    if ((confirmParam === "storage_scan" || confirmParam === "purview") && can("org:admin")) setConfirmFor({ scope: confirmParam });
  }, [confirmParam, user]); // eslint-disable-line react-hooks/exhaustive-deps

  const visible = useMemo(() => {
    if (!data) return [];
    const needle = q.trim().toLowerCase();
    return data.findings.filter((f) => {
      if (f.id === expanded) return true;
      if (status ? f.status !== status : f.status === "remediated") return false;
      if (category && !matchesCategory(f, category)) return false;
      if (source && f.source !== source) return false;
      if (needle && !f.location.toLowerCase().includes(needle)) return false;
      return true;
    });
  }, [data, q, status, category, source, expanded]);

  const lookAgain = async () => {
    setRunState("busy"); setRunError(null);
    try {
      await api.post("/data-discovery/run", {});
      setRunState("queued");
      if (reloadTimer.current) window.clearTimeout(reloadTimer.current);
      reloadTimer.current = window.setTimeout(() => { setRunState("idle"); load(); }, 20_000);
    } catch (e) { setRunError(apiErrorMessage(e, "Could not start a new look")); setRunState("idle"); }
  };

  const heading = (
    <>
      <h1 style={{ ...pageHeading, display: "flex", alignItems: "center", gap: 8 }}>
        <ScanSearch size={20} /> Sensitive data
      </h1>
      <p style={{ ...pageSubtitle, maxWidth: 760 }}>
        Where personal and sensitive data sits across Microsoft 365, your cloud storage and your connected folders. You see what kind and how much, never the data itself{iso27001Citation(enrolled, "A.5.12, A.5.34")}; the record of processing.
      </p>
    </>
  );

  if (error && !data) return <div className="page-pad">{heading}<div style={{ marginTop: 16, ...text.body, color: theme.color.dangerText }}>{error}</div></div>;
  if (!data) return <div className="page-pad">{heading}<LoadingBlock /></div>;

  const live = data.findings.filter((f) => f.status !== "remediated");
  const highCount = live.filter((f) => f.severity === "high").length;
  const openCount = data.findings.filter((f) => f.status === "open").length;
  const mappedCount = data.findings.filter((f) => f.status === "mapped").length;
  const ropa = data.recordsOfProcessing !== false;
  const catLabel = (id: string) => data.vocabulary.categories.find((c) => c.id === id)?.label ?? id;
  const catTiles: Array<{ key: string; label: string; count: number; sub: string }> = [
    { key: "credentials", label: "Secrets and credentials", count: live.filter((f) => has(f, "credentials")).length, sub: "locations holding passwords or keys" },
    { key: "special", label: "Health / special category", count: live.filter((f) => matchesCategory(f, "special")).length, sub: "locations holding Art. 9 data" },
    { key: "criminal_offence", label: "Criminal offence data", count: live.filter((f) => has(f, "criminal_offence")).length, sub: "locations holding Art. 10 data" },
    { key: "payment_card", label: "Card numbers", count: live.filter((f) => has(f, "payment_card")).length, sub: "locations holding card numbers" },
  ].filter((t) => t.count > 0);
  const sourceLabel = (id: string) => data.sources.find((s) => s.id === id)?.label ?? id;

  return (
    <div className="page-pad">
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div>{heading}</div>
        {can("privacy:write") && (
          <div style={{ display: "grid", justifyItems: "end", gap: 4 }}>
            <button type="button" onClick={lookAgain} disabled={runState !== "idle"} style={{ ...btnPrimary, display: "flex", alignItems: "center", gap: 6, opacity: runState !== "idle" ? 0.6 : 1 }}>
              <RefreshCw size={14} /> {runState === "busy" ? "Starting…" : "Look again now"}
            </button>
            {runState === "queued" && <span style={text.hint}>Looking — results appear here shortly.</span>}
            {runError && <span style={{ ...text.hint, color: theme.color.dangerText }}>{runError}</span>}
          </div>
        )}
      </div>

      <div style={{ display: "grid", gap: 20, marginTop: 20 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 }}>
          <Tile label="Locations with sensitive data" value={live.length} sub="not removed or fixed" onClick={() => { setParam("status", ""); setParam("category", ""); }} />
          <Tile label="High severity" value={highCount} danger={highCount > 0} sub="secrets, Art. 9, Art. 10 or card data" />
          <Tile label="Not yet reviewed" value={openCount} sub="still to review" onClick={() => setParam("status", "open")} />
          {ropa && <Tile label="Linked to a processing record" value={mappedCount} sub="in your records of processing" onClick={() => setParam("status", "mapped")} />}
          {catTiles.map((t) => (
            <Tile key={t.key} label={t.label} value={t.count} danger sub={t.sub} onClick={() => setParam("category", t.key)} />
          ))}
        </div>

        {data.attention.length > 0 && (
          <div style={{ ...card, padding: 0 }}>
            <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--c-border)", display: "flex", alignItems: "center", gap: 8, ...sectionTitle }}>
              <AlertTriangle size={15} /> Needs attention
            </div>
            {data.attention.map((a) => (
              <PortalLink key={a.id} to={a.href} style={{ textDecoration: "none" }}>
                <div style={{ display: "flex", gap: 12, padding: "11px 18px", borderBottom: "1px solid var(--c-border)", alignItems: "flex-start" }}>
                  <span style={{ width: 8, height: 8, borderRadius: 999, marginTop: 6, background: SEV_DOT[a.severity], flexShrink: 0 }} />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={text.itemTitle}>{a.title}</div>
                    <div style={{ ...text.hint, marginTop: 2 }}>{a.description}</div>
                  </div>
                  {a.dueAt && <span style={{ fontSize: text.meta.fontSize, color: a.severity === "high" ? theme.color.dangerText : theme.color.muted, whiteSpace: "nowrap" }}>{relativeDue(a.dueAt)}</span>}
                </div>
              </PortalLink>
            ))}
          </div>
        )}

        {data.findings.length === 0 ? (
          <div style={card}>
            <EmptyState
              title="Nothing found yet"
              detail="There are three ways to fill this page. Connect Microsoft 365 Security with the Purview DLP permission, and the platform reads what Microsoft's own classifiers find in SharePoint, OneDrive, Teams and email. Connect AWS Security Hub with Macie, or Google Cloud with Sensitive Data Protection, for your cloud storage. Or switch on the connected storage scan below, which counts personal data in the files imported from your connected folders."
              action={<PortalLink to="/connections" style={{ ...btnPrimary, textDecoration: "none" }}>Open connections</PortalLink>}
            />
          </div>
        ) : (
          <div style={{ display: "grid", gap: 10 }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
              <select value={category} onChange={(e) => setParam("category", e.target.value)} style={{ ...input, width: "auto" }} aria-label="Kind of data">
                <option value="">All kinds of data</option>
                <option value="special">Health or special category</option>
                {data.vocabulary.categories.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
              <select value={status} onChange={(e) => setParam("status", e.target.value)} style={{ ...input, width: "auto" }} aria-label="Status">
                <option value="">Everything not yet fixed</option>
                {data.vocabulary.statuses.map((s) => <option key={s} value={s}>{STATUS_LABEL[s] ?? s}</option>)}
              </select>
              <select value={source} onChange={(e) => setSource(e.target.value)} style={{ ...input, width: "auto" }} aria-label="Source">
                <option value="">All sources</option>
                {data.sources.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search location" style={{ ...input, width: 220, flex: "1 1 180px", maxWidth: 320 }} />
              <span style={{ ...text.hint, marginLeft: "auto" }}>{visible.length} location{visible.length === 1 ? "" : "s"}</span>
            </div>

            <div style={{ ...card, padding: 0, overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: text.body.fontSize }}>
                <thead><tr style={headRow}>
                  {["", "Location", "Source", "Data found", "Severity", "Status", ...(ropa ? ["Processing record"] : []), "Last seen"].map((h, i) => <th key={i} style={th}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {visible.map((f) => (
                    <FindingRows key={f.id} f={f} ropa={ropa} open={expanded === f.id} catLabel={catLabel} sourceLabel={sourceLabel(f.source)}
                      onToggle={() => setExpanded(expanded === f.id ? null : f.id)}
                      canReview={can("privacy:write")} onReview={() => setReviewFor(f)} />
                  ))}
                  {visible.length === 0 && <tr><td colSpan={ropa ? 8 : 7} style={{ padding: 20, color: theme.color.muted }}>Nothing matches these filters.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <SourcesPanel sources={data.sources} canAdmin={can("org:admin")} onSaved={load} ropa={ropa}
          onConfirm={(scope, then) => setConfirmFor({ scope, then })} />

        <div style={{ ...card, padding: 18, display: "flex", gap: 10, alignItems: "flex-start" }}>
          <Info size={15} style={{ color: theme.color.muted, flexShrink: 0, marginTop: 2 }} />
          <div style={{ ...text.hint, lineHeight: 1.6 }}>
            <strong style={{ color: theme.color.text }}>What is stored.</strong> The kind of data and a count per location, never the values themselves. Email is counted for the whole tenant, never per message. SharePoint and OneDrive are recorded by folder, and a scanned file by its folder and a reference, not by its name, because a file name can itself be personal data; the file's name is shown only to members who manage the data protection registers, and so is whose OneDrive a location is{data.locationDetail === false ? " (you see the counts)" : ""}.
          </div>
        </div>
      </div>

      {confirmFor && (
        <ConfirmMonitoringModal scope={confirmFor.scope} bases={data.vocabulary.lawfulBases ?? []} decisions={data.vocabulary.dpiaDecisions ?? []}
          current={data.sources.find((s) => s.id === confirmFor.scope)?.confirmation ?? null}
          onClose={() => { setConfirmFor(null); setParam("confirm", ""); }}
          onConfirm={async (c) => {
            if (confirmFor.then) await confirmFor.then(c);
            else await api.post("/data-discovery/confirmations", c);
            setConfirmFor(null); setParam("confirm", ""); load();
          }} />
      )}

      {reviewFor && (
        <ReviewModal finding={reviewFor} ropa={ropa} ropaEntries={data.ropaEntries} assets={data.assets ?? []}
          onClose={() => setReviewFor(null)} onSaved={() => { setReviewFor(null); load(); }} />
      )}
    </div>
  );
}

// ── Pieces ────────────────────────────────────────────────────────────────────

function Tile({ label, value, sub, danger, onClick }: { label: string; value: string | number; sub?: string; danger?: boolean; onClick?: () => void }) {
  return (
    <div onClick={onClick} role={onClick ? "button" : undefined} style={{ ...card, padding: "14px 16px", height: "100%", cursor: onClick ? "pointer" : undefined, borderColor: danger ? theme.color.danger : undefined }}>
      <div style={text.statLabel}>{label}</div>
      <div style={{ ...text.stat, color: danger ? theme.color.dangerText : theme.color.heading, marginTop: 4 }}>{value}</div>
      {sub && <div style={{ ...text.hint, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function CategoryChips({ categories, catLabel }: { categories: Record<string, number>; catLabel: (id: string) => string }) {
  const entries = Object.entries(categories ?? {}).filter(([, n]) => n > 0).sort((a, b) => Number(DANGER_CATS.has(b[0])) - Number(DANGER_CATS.has(a[0])) || b[1] - a[1]);
  if (entries.length === 0) return <span style={{ color: theme.color.faint }}>—</span>;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
      {entries.map(([id, n]) => {
        const s = catStyle(id);
        return <span key={id} style={pill(s.bg, s.color)}>{catLabel(id)} · {n}</span>;
      })}
    </div>
  );
}

function FindingRows({ f, ropa = true, open, catLabel, sourceLabel, onToggle, canReview, onReview }: {
  f: Finding; ropa?: boolean; open: boolean; catLabel: (id: string) => string; sourceLabel: string; onToggle: () => void; canReview: boolean; onReview: () => void;
}) {
  const url = safeHttps(f.locationUrl);
  const st = statusStyle(f.status);
  const types = Object.entries(f.detail?.types ?? {}).sort((a, b) => b[1] - a[1]);

  return (
    <>
      <tr onClick={onToggle} style={{ borderBottom: open ? "none" : "1px solid var(--c-border)", cursor: "pointer", opacity: f.status === "remediated" ? 0.7 : 1 }}>
        <td style={{ ...td, paddingRight: 0, color: theme.color.muted }}>{open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</td>
        <td style={{ ...td, minWidth: 220 }}>
          {url ? (
            <a href={url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} style={{ color: theme.color.accentText, fontWeight: 600, textDecoration: "none", overflowWrap: "anywhere", display: "inline-flex", alignItems: "center", gap: 4 }}>
              {f.location} <ExternalLink size={11} />
            </a>
          ) : (
            <div style={{ fontWeight: 600, color: theme.color.text, overflowWrap: "anywhere" }}>{f.location}</div>
          )}
          <div style={{ ...text.meta, marginTop: 2 }}>{KIND_LABEL[f.locationKind] ?? "Other"}</div>
        </td>
        <td style={{ ...td, color: theme.color.muted, whiteSpace: "nowrap" }}>{sourceLabel}</td>
        <td style={{ ...td, minWidth: 200 }}><CategoryChips categories={f.categories} catLabel={catLabel} /></td>
        <td style={td}><SeverityPill value={f.severity} /></td>
        <td style={td}>
          <span style={pill(st.bg, st.color)}>{STATUS_LABEL[f.status] ?? f.status}</span>
          {f.noLongerReportedAt && <div style={{ ...text.meta, marginTop: 4 }}>No longer reported</div>}
        </td>
        {ropa && <td style={{ ...td, color: f.ropaEntry ? theme.color.text : theme.color.faint }}>{f.ropaEntry?.name ?? "—"}</td>}
        <td style={{ ...td, color: theme.color.muted, whiteSpace: "nowrap" }}>{fmtDay(f.lastSeenAt)}</td>
      </tr>
      {open && (
        <tr style={{ borderBottom: "1px solid var(--c-border)" }}>
          <td />
          <td colSpan={ropa ? 7 : 6} style={{ padding: "0 14px 14px" }}>
            <div style={{ background: "var(--c-panelAlt)", borderRadius: 3, padding: 14 }}>
              {/* Every location holding personal data is either on a processing record, expected, or cleaned up. */}
              {f.status === "open" && (
                <NextSteps items={[
                  { done: false, label: ropa ? "Map it to a processing record, mark it expected, or remediate it" : "Mark it expected, or remediate it", urgent: f.severity === "high" || f.severity === "critical" },
                ]} />
              )}
              <DetailSection title="Location">
                <DetailFields fields={[
                  { label: "Location", value: url ? <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: theme.color.accentText, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4 }}>{f.location} <ExternalLink size={11} /></a> : f.location, wide: true },
                  { label: "Kind", value: KIND_LABEL[f.locationKind] ?? "Other" },
                  { label: "Source", value: sourceLabel },
                  { label: "Severity", value: <SeverityPill value={f.severity} /> },
                  { label: "Items", value: f.itemCount ? String(f.itemCount) : null },
                  { label: "File", wide: true, value: f.fileName ? (safeHttps(f.fileUrl ?? null)
                    ? <a href={safeHttps(f.fileUrl ?? null)!} target="_blank" rel="noopener noreferrer" style={{ color: theme.color.accentText, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4 }}>{f.fileName} <ExternalLink size={11} /></a>
                    : f.fileName) : null },
                ]} />
              </DetailSection>
              <DetailSection title="Data found">
                <DetailFields fields={[
                  { label: "Kinds of data", value: <CategoryChips categories={f.categories} catLabel={catLabel} />, wide: true },
                  { label: "What the source reported", wide: true, value: types.length > 0 ? (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                      {types.map(([name, n]) => <span key={name} style={pill(theme.color.panel, theme.color.text)}>{name} · {n}</span>)}
                    </div>
                  ) : null },
                ]} />
              </DetailSection>
              <DetailSection title="Review">
                <DetailFields fields={[
                  { label: "Status", value: <span style={pill(st.bg, st.color)}>{STATUS_LABEL[f.status] ?? f.status}</span> },
                  ...(!ropa ? [] : [{ label: "Processing record", value: f.ropaEntry ? <PortalLink to="/gdpr?tab=ropa" style={{ color: theme.color.accentText, textDecoration: "none" }}>{f.ropaEntry.name}</PortalLink> : null }]),
                  { label: "Asset", value: f.asset?.name ?? null },
                  { label: "Reviewed", value: f.reviewedAt ? fmtDay(f.reviewedAt) : "Not reviewed yet" },
                  { label: "Review note", value: f.reviewNote, wide: true, multiline: true },
                ]} />
              </DetailSection>
              <DetailSection title="Timeline">
                <DetailFields fields={[
                  { label: "First seen", value: fmtDay(f.firstSeenAt) },
                  { label: "Last seen", value: fmtDateTime(f.lastSeenAt) },
                  { label: "Recorded as fixed", value: f.resolvedAt ? fmtDay(f.resolvedAt) : null },
                  { label: "No longer reported by the source", value: f.noLongerReportedAt ? `${fmtDay(f.noLongerReportedAt)}: not taken to mean the data has gone. Check, then record what you found.` : null, wide: true },
                ]} />
              </DetailSection>
              {canReview && (
                <RecordActions note={ropa ? "Link it to a processing record, mark it expected or record it as removed." : "Mark it expected or record it as removed."}>
                  <button type="button" onClick={(e) => { e.stopPropagation(); onReview(); }} style={secondaryBtn}>Review</button>
                </RecordActions>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ── Review ────────────────────────────────────────────────────────────────────

const REVIEW_OPTIONS: Array<{ id: Status; label: string; help: string }> = [
  { id: "mapped", label: "Linked to a processing record", help: "This data belongs to a processing activity you have recorded." },
  { id: "expected", label: "Expected — we know and accept it", help: "Say why it is right for this data to be here. Health, special category or criminal offence data needs the processing record it belongs to." },
  { id: "remediated", label: "Removed or fixed", help: "Say what was done. If the source finds it again, it opens again." },
  { id: "open", label: "Still to review", help: "Leave it on the list." },
];

function ReviewModal({ finding, ropa = true, ropaEntries, assets, onClose, onSaved }: {
  ropa?: boolean;
  finding: Finding; ropaEntries: DiscoveryResponse["ropaEntries"]; assets: NonNullable<DiscoveryResponse["assets"]>; onClose: () => void; onSaved: () => void;
}) {
  const [status, setStatus] = useState<Status>(finding.status === "open" ? "mapped" : (finding.status as Status));
  const [ropaEntryId, setRopaEntryId] = useState(finding.ropaEntry?.id ?? "");
  // The system that holds the data — pre-filled where discovery matched the location to an asset.
  const [assetId, setAssetId] = useState(finding.asset?.id ?? "");
  const [note, setNote] = useState(finding.reviewNote ?? "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Art. 9 / Art. 10 data is settled only on a record that declares it (the server applies the same rule).
  const sensitive = ropa && SENSITIVE_CATS.some((c) => (finding.categories?.[c] ?? 0) > 0);
  const needsRecord = status === "mapped" || (status === "expected" && sensitive);
  const chosen = ropaEntries.find((r) => r.id === ropaEntryId);
  const declared = Array.isArray(chosen?.dataCategories) ? (chosen!.dataCategories as string[]) : [];
  const undeclared = !sensitive || !chosen ? [] : [
    (finding.categories?.health ?? 0) > 0 && !declared.some((d) => d === "health" || d === "special_category") ? "health data" : null,
    (finding.categories?.special_category ?? 0) > 0 && !declared.some((d) => d !== "health" && ["racial_ethnic_origin", "political_opinions", "religious_beliefs", "trade_union_membership", "genetic", "biometric", "sex_life", "sexual_orientation", "special_category"].includes(d)) ? "special category data" : null,
    (finding.categories?.criminal_offence ?? 0) > 0 && !declared.includes("criminal_offence") ? "criminal offence data" : null,
  ].filter((x): x is string => !!x);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (needsRecord && !ropaEntryId) { setErr("Choose the processing record this data belongs to"); return; }
    if ((status === "expected" || status === "remediated") && !note.trim()) { setErr("Say why, so the record explains itself"); return; }
    setSaving(true); setErr(null);
    try {
      await api.post(`/data-discovery/findings/${finding.id}`, {
        status,
        ropaEntryId: needsRecord || (status === "expected" && ropaEntryId) ? ropaEntryId || null : undefined,
        assetId: assetId || null,
        note: note.trim() || null,
      });
      onSaved();
    } catch (e2) { setErr(apiErrorMessage(e2, "Could not save the review")); setSaving(false); }
  };

  return (
    <ModalOverlay onClose={onClose}>
      <ModalHeader title="Review this location" onClose={onClose} />
      <form onSubmit={submit} style={{ padding: 20, display: "grid", gap: 14 }}>
        <div style={{ ...text.body, color: theme.color.muted, lineHeight: 1.5, overflowWrap: "anywhere" }}>
          <strong style={{ color: theme.color.text }}>{finding.location}</strong>
        </div>
        <div style={{ display: "grid", gap: 8 }}>
          {REVIEW_OPTIONS.filter((o) => ropa || o.id !== "mapped").map((o) => (
            <label key={o.id} style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "10px 12px", borderRadius: 3, border: `1px solid ${status === o.id ? "var(--c-accent)" : "var(--c-border)"}`, background: status === o.id ? "var(--c-accentBg)" : undefined, cursor: "pointer" }}>
              <input type="radio" name="review-status" checked={status === o.id} onChange={() => { setStatus(o.id); setErr(null); }} style={{ marginTop: 3 }} />
              <span>
                <span style={{ display: "block", ...text.body, fontWeight: 600 }}>{o.label}</span>
                <span style={{ display: "block", ...text.hint, marginTop: 2 }}>{o.help}</span>
              </span>
            </label>
          ))}
        </div>

        {(needsRecord || (ropa && status === "expected")) && (
          ropaEntries.length === 0 ? (
            <div style={{ ...text.body, color: theme.color.muted, lineHeight: 1.5 }}>
              You have no processing records yet. Record the processing activity this data belongs to first, then come back and link it.{" "}
              <PortalLink to="/gdpr?tab=ropa" style={{ color: theme.color.accentText }}>Open the records of processing</PortalLink>
            </div>
          ) : (
            <Field label={needsRecord ? "Processing record" : "Processing record (optional)"}>
              <select value={ropaEntryId} onChange={(e) => setRopaEntryId(e.target.value)} style={input}>
                <option value="">Choose a record</option>
                {ropaEntries.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
              {undeclared.length > 0 && (
                <div style={{ ...text.hint, color: theme.color.warningText, marginTop: 6, lineHeight: 1.5 }}>
                  This record does not declare {undeclared.join(" or ")}. {status === "expected" ? "Add it to the record, with its condition, before accepting the location as expected." : "You can link it, but it stays on the list until the record declares it."}
                </div>
              )}
            </Field>
          )
        )}

        <Field label="System that holds it (asset register)">
          <select value={assetId} onChange={(e) => setAssetId(e.target.value)} style={input}>
            <option value="">Not linked</option>
            {assetId && !assets.some((a) => a.id === assetId) && <option value={assetId}>{finding.asset?.name ?? "Linked asset"}</option>}
            {assets.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </Field>

        <Field label={status === "expected" || status === "remediated" ? "Why (required)" : "Note (optional)"}>
          <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} style={textarea}
            placeholder={status === "remediated" ? "For example: spreadsheet moved to the HR system and deleted on 12 September" : status === "expected" ? "For example: the payroll team's folder, access limited to payroll" : ""} />
        </Field>

        {err && <div style={{ ...text.body, color: theme.color.dangerText }}>{err}</div>}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button type="button" onClick={onClose} style={secondaryBtn}>Cancel</button>
          <button type="submit" disabled={saving || (needsRecord && ropaEntries.length === 0)} style={{ ...btnPrimary, opacity: saving || (needsRecord && ropaEntries.length === 0) ? 0.6 : 1 }}>{saving ? "Saving…" : "Save"}</button>
        </div>
      </form>
    </ModalOverlay>
  );
}

// ── Sources ───────────────────────────────────────────────────────────────────

function runLine(run: Run | null): { text: string; color: string } {
  if (!run) return { text: "Not run yet", color: theme.color.faint };
  const when = fmtDateTime(run.finishedAt ?? run.startedAt);
  if (run.status === "error") return { text: `Failed ${when}${run.error ? `: ${run.error}` : ""}`, color: theme.color.dangerText };
  if (run.status === "running") return { text: `Running since ${fmtDateTime(run.startedAt)}`, color: theme.color.muted };
  if (run.status === "skipped") return { text: `Skipped ${when}${run.error ? `: ${run.error}` : ""}`, color: theme.color.muted };
  return { text: `Last run ${when}: ${run.findings} location${run.findings === 1 ? "" : "s"} found${run.scanned ? `, ${run.scanned} checked` : ""}`, color: theme.color.muted };
}

function Toggle({ checked, disabled, onChange, label }: { checked: boolean; disabled?: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <label style={{ display: "inline-flex", alignItems: "center", gap: 6, ...text.hint, color: theme.color.text, cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.6 : 1 }}>
      <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} /> {label}
    </label>
  );
}

const needsConfirmation = (e: unknown) => e instanceof ApiError && e.status === 409 && e.code === "confirmation_required";

function ConfirmationLine({ c }: { c: Confirmation | null | undefined }) {
  if (!c) return <div style={{ ...text.hint, color: theme.color.warningText }}>No lawful basis, staff notice or DPIA decision recorded yet.</div>;
  return <div style={text.hint}>Basis, staff notice and DPIA decision recorded {fmtDay(c.confirmedAt)}{c.confirmedBy ? ` by ${c.confirmedBy.name}` : ""}.</div>;
}

function SourcesPanel({ sources, canAdmin, onSaved, ropa, onConfirm }: {
  sources: Source[]; canAdmin: boolean; onSaved: () => void; ropa: boolean;
  onConfirm: (scope: ConfirmScope, then?: (c: Record<string, unknown>) => Promise<void>) => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // Switching a monitoring source on asks for its basis first when none is recorded; the server refuses otherwise.
  const send = async (id: string, body: Record<string, unknown>, scope: ConfirmScope | null, failure: string) => {
    setBusy(id); setErr(null);
    try { await api.put("/data-discovery/settings", body); onSaved(); }
    catch (e) {
      if (scope && needsConfirmation(e)) onConfirm(scope, async (confirmation) => { await api.put("/data-discovery/settings", { ...body, confirmation }); onSaved(); });
      else setErr(apiErrorMessage(e, failure));
    }
    setBusy(null);
  };

  const setVendor = (id: SourceId, enabled: boolean) => {
    const disabled = sources.filter((s) => VENDOR_SOURCES.includes(s.id) && (s.id === id ? !enabled : !s.enabled)).map((s) => s.id);
    return send(id, { disabledSources: disabled }, enabled && id === "purview" ? "purview" : null, "Could not change the source");
  };

  const setScan = (enabled: boolean) => {
    if (enabled && !window.confirm("Switch on the connected storage scan? The platform will read the contents of files imported from your connected storage, including staff files, to count personal and sensitive data. Your organisation is the controller of this processing: it needs a lawful basis, staff should be told, and you should decide whether it needs a DPIA. Only the counts are kept, never the data. You can switch it off at any time.")) return;
    return send("storage_scan", { scanEnabled: enabled }, enabled ? "storage_scan" : null, "Could not change the scan");
  };

  const vendors = sources.filter((s) => s.id !== "storage_scan");
  const scan = sources.find((s) => s.id === "storage_scan");

  return (
    <div style={{ display: "grid", gap: 10 }}>
      <div style={sectionTitle}>Sources</div>
      {err && <div style={{ ...text.body, color: theme.color.dangerText }}>{err}</div>}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 12 }}>
        {vendors.map((s) => {
          const r = runLine(s.lastRun);
          return (
            <div key={s.id} style={{ ...card, padding: 16, display: "grid", gap: 8, alignContent: "start" }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <div style={{ ...text.body, fontWeight: 600 }}>{s.label}</div>
                <span style={s.connected ? pill(theme.color.successBg, theme.color.successText) : pill(theme.color.panelAlt, theme.color.muted)}>{s.connected ? "Connected" : "Not connected"}</span>
              </div>
              <div style={{ fontSize: text.hint.fontSize, color: r.color, overflowWrap: "anywhere" }}>{r.text}</div>
              <div style={{ ...text.meta, lineHeight: 1.5 }}>Needs: {s.needs}.</div>
              {s.coverage && <div style={{ ...text.hint, lineHeight: 1.5 }}>{s.coverage}</div>}
              {ropa && s.id === "purview" && s.enabled && s.connected && (
                <>
                  <ConfirmationLine c={s.confirmation} />
                  {canAdmin && <button type="button" onClick={() => onConfirm("purview")} style={{ ...secondaryBtn, justifySelf: "start" }}>{s.confirmation ? "Update the record" : "Record the basis"}</button>}
                </>
              )}
              {canAdmin ? (
                <Toggle checked={s.enabled} disabled={busy === s.id} onChange={(v) => setVendor(s.id, v)} label="Read findings from this source" />
              ) : (
                <div style={text.hint}>{s.enabled ? "Switched on" : "Switched off"}</div>
              )}
              {!s.connected && <PortalLink to="/connections" style={{ ...text.hint, color: theme.color.accentText }}>Connect</PortalLink>}
            </div>
          );
        })}
      </div>

      {scan && (() => {
        const r = runLine(scan.lastRun);
        return (
          <div style={{ ...card, padding: 16, display: "grid", gap: 8, borderColor: scan.enabled ? "var(--c-accent)" : undefined }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <div style={{ ...text.body, fontWeight: 600 }}>{scan.label}</div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                <span style={scan.connected ? pill(theme.color.successBg, theme.color.successText) : pill(theme.color.panelAlt, theme.color.muted)}>{scan.connected ? "Storage connected" : "No storage connected"}</span>
                <span style={scan.enabled ? pill(theme.color.accentBg, theme.color.accentText) : pill(theme.color.panelAlt, theme.color.muted)}>{scan.enabled ? "Scan on" : "Scan off"}</span>
              </div>
            </div>
            <div style={{ ...text.hint, lineHeight: 1.6, maxWidth: 760 }}>
              Off unless you switch it on. When on, the platform reads the contents of files it imports from your connected storage (OneDrive, SharePoint, Google Drive or the Microsoft 365 folder) and counts personal and sensitive data in them. It keeps only the counts, per folder, never the data, and skips your own policy documents. Reading staff files is processing your organisation is responsible for: switching it on asks you to record its lawful basis, how staff were told and the DPIA decision. You can switch it off at any time.
            </div>
            {ropa && scan.enabled && (
              <>
                <ConfirmationLine c={scan.confirmation} />
                {canAdmin && <button type="button" onClick={() => onConfirm("storage_scan")} style={{ ...secondaryBtn, justifySelf: "start" }}>{scan.confirmation ? "Update the record" : "Record the basis"}</button>}
              </>
            )}
            <div style={{ fontSize: text.hint.fontSize, color: r.color, overflowWrap: "anywhere" }}>{r.text}</div>
            <div style={{ ...text.meta, lineHeight: 1.5 }}>Needs: {scan.needs}.</div>
            {canAdmin ? (
              <div>
                <button type="button" disabled={busy === "storage_scan"} onClick={() => setScan(!scan.enabled)} style={{ ...(scan.enabled ? secondaryBtn : btnPrimary), opacity: busy === "storage_scan" ? 0.6 : 1 }}>
                  {busy === "storage_scan" ? "Saving…" : scan.enabled ? "Switch the scan off" : "Switch the scan on"}
                </button>
              </div>
            ) : (
              <div style={text.hint}>An organisation administrator can switch the scan on or off.</div>
            )}
            {!scan.connected && <PortalLink to="/connections" style={{ ...text.hint, color: theme.color.accentText }}>Connect storage</PortalLink>}
          </div>
        );
      })()}
    </div>
  );
}

// ── Monitoring confirmation ───────────────────────────────────────────────────

function ConfirmMonitoringModal({ scope, bases, decisions, current, onClose, onConfirm }: {
  scope: ConfirmScope; bases: Choice[]; decisions: Choice[]; current: Confirmation | null;
  onClose: () => void; onConfirm: (c: Record<string, unknown>) => Promise<void>;
}) {
  const [lawfulBasis, setLawfulBasis] = useState(current?.lawfulBasis ?? "");
  const [basisNote, setBasisNote] = useState(current?.basisNote ?? "");
  const [staffInformed, setStaffInformed] = useState(!!current);
  const [staffNotice, setStaffNotice] = useState(current?.staffNotice ?? "");
  const [dpiaDecision, setDpiaDecision] = useState(current?.dpiaDecision ?? "");
  const [dpiaNote, setDpiaNote] = useState(current?.dpiaNote ?? "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const what = scope === "storage_scan"
    ? "The connected storage scan reads the contents of every file imported from your connected storage, staff files included."
    : "Microsoft Purview DLP over Exchange email and Teams inspects staff communications: it is monitoring of workers.";

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true); setErr(null);
    try { await onConfirm({ scope, lawfulBasis, basisNote, staffInformed, staffNotice, dpiaDecision, dpiaNote: dpiaNote || null }); }
    catch (e2) { setErr(apiErrorMessage(e2, "Could not record it")); setSaving(false); }
  };

  return (
    <ModalOverlay onClose={onClose}>
      <ModalHeader title={scope === "storage_scan" ? "Why you scan connected storage" : "Why you read Purview DLP findings"} onClose={onClose} />
      <form onSubmit={submit} style={{ padding: 20, display: "grid", gap: 14 }}>
        <div style={{ ...text.body, color: theme.color.muted, lineHeight: 1.5, maxWidth: 760 }}>
          {what} Your organisation is the controller of this processing and decides whether it is lawful; the platform records what you decided and who decided it. The ICO's guidance on monitoring workers expects a lawful basis, workers to be told, and says a DPIA is likely to be needed.
        </div>
        <Field label="Lawful basis">
          <select value={lawfulBasis} onChange={(e) => setLawfulBasis(e.target.value)} style={input}>
            <option value="">Choose the basis</option>
            {bases.map((b) => <option key={b.id} value={b.id}>{b.label}</option>)}
          </select>
        </Field>
        <Field label={lawfulBasis === "legitimate_interests" ? "Where the balancing test is recorded" : "What the basis rests on"}>
          <textarea rows={2} value={basisNote} onChange={(e) => setBasisNote(e.target.value)} style={textarea} placeholder="For example: legitimate interests assessment LIA-07, approved by the DPO on 3 October" />
        </Field>
        <label style={{ display: "flex", gap: 8, alignItems: "flex-start", ...text.body }}>
          <input type="checkbox" checked={staffInformed} onChange={(e) => setStaffInformed(e.target.checked)} style={{ marginTop: 3 }} />
          <span>Staff have been told that their {scope === "storage_scan" ? "files" : "email and Teams messages"} are read for this purpose</span>
        </label>
        <Field label="How staff were told">
          <textarea rows={2} value={staffNotice} onChange={(e) => setStaffNotice(e.target.value)} style={textarea} placeholder="For example: staff privacy notice section 6, updated and circulated on 1 October" />
        </Field>
        <Field label="DPIA decision">
          <select value={dpiaDecision} onChange={(e) => setDpiaDecision(e.target.value)} style={input}>
            <option value="">Choose</option>
            {decisions.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
          </select>
        </Field>
        <Field label={dpiaDecision === "not_required" ? "Why no DPIA is needed (required)" : "DPIA reference or note (optional)"}>
          <textarea rows={2} value={dpiaNote} onChange={(e) => setDpiaNote(e.target.value)} style={textarea} />
        </Field>
        {err && <div style={{ ...text.body, color: theme.color.dangerText }}>{err}</div>}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button type="button" onClick={onClose} style={secondaryBtn}>Cancel</button>
          <button type="submit" disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}>{saving ? "Saving…" : "Record it"}</button>
        </div>
      </form>
    </ModalOverlay>
  );
}
